import { type KeyboardEvent } from 'react';
import {
  CheckCircle2,
  HelpCircle,
  Lightbulb,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';

import { Avatar } from './Avatar';
import type { InboxItem, InboxItemKind } from '../hooks/use-inbox';

export interface InboxListItemProps {
  item: InboxItem;
  active: boolean;
  density: 'comfy' | 'compact';
  onSelect: (id: string) => void;
}

const KIND_META: Record<
  InboxItemKind,
  { label: string; Icon: typeof ShieldCheck; tone: string; bar: string }
> = {
  approval: {
    label: 'Approval',
    Icon: ShieldCheck,
    tone: 'text-sd-info',
    bar: 'bg-sd-info',
  },
  review: {
    label: 'Review',
    Icon: CheckCircle2,
    tone: 'text-sd-success',
    bar: 'bg-sd-success',
  },
  question: {
    label: 'Question',
    Icon: HelpCircle,
    tone: 'text-sd-warning',
    bar: 'bg-sd-warning',
  },
  failure: {
    label: 'Needs you',
    Icon: TriangleAlert,
    tone: 'text-sd-error',
    bar: 'bg-sd-error',
  },
  suggestion: {
    label: 'Suggestion',
    Icon: Lightbulb,
    tone: 'text-sd-text-secondary',
    bar: 'bg-sd-text-secondary/60',
  },
};

function relativeTime(iso: string): string {
  const delta = Date.now() - new Date(iso).getTime();
  const min = Math.round(delta / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.round(hr / 24);
  if (d < 7) return `${d}d`;
  return `${Math.round(d / 7)}w`;
}

export function InboxListItem({ item, active, density, onSelect }: InboxListItemProps) {
  const meta = KIND_META[item.kind];
  const { Icon } = meta;
  const resolved = !!item.resolved_at;

  const handleKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(item.id);
    }
  };

  const padY = density === 'compact' ? 'py-sd-2' : 'py-sd-3';
  const titleClass = density === 'compact' ? 'text-sd-sm' : 'text-sd-sm';
  const summaryClass = density === 'compact' ? 'hidden' : 'text-sd-xs text-sd-text-secondary mt-sd-1 line-clamp-2';

  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      data-testid={`inbox-item-${item.id}`}
      data-kind={item.kind}
      data-unread={item.unread ? 'true' : 'false'}
      data-resolved={resolved ? 'true' : 'false'}
      onClick={() => onSelect(item.id)}
      onKeyDown={handleKey}
      className={[
        'relative w-full text-left px-sd-4',
        padY,
        'border-b border-sd-border/30',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1',
        'transition-colors duration-150 motion-reduce:transition-none',
        active
          ? 'bg-sd-primary/10'
          : 'hover:bg-sd-surface-elevated/60',
        resolved ? 'opacity-60' : '',
      ].join(' ')}
    >
      {/* Priority / kind accent rail */}
      <span
        aria-hidden
        className={`absolute left-0 top-0 bottom-0 w-[3px] ${active ? meta.bar : resolved ? 'bg-transparent' : meta.bar + '/70'}`}
      />

      <div className="flex items-start gap-sd-3">
        <Avatar
          initials={item.agent.initials}
          color={item.agent.color}
          size={density === 'compact' ? 'sm' : 'md'}
        />
        <div className="min-w-0 flex-1">
          {/* Top row: kind chip · agent · time */}
          <div className="flex items-center gap-sd-2 text-sd-xs text-sd-text-secondary">
            <span className={`inline-flex items-center gap-sd-1 font-medium ${meta.tone}`}>
              <Icon size={12} aria-hidden /> {meta.label}
            </span>
            <span aria-hidden>·</span>
            <span className="truncate">{item.agent.name}</span>
            <span aria-hidden>·</span>
            <span className="whitespace-nowrap">{relativeTime(item.received_at)}</span>
            {item.unread && (
              <span
                aria-label="unread"
                className="ml-auto inline-block w-sd-2 h-sd-2 rounded-full bg-sd-warning"
              />
            )}
            {resolved && (
              <span className="ml-auto text-sd-xs text-sd-text-secondary italic">
                {item.resolved_action}
              </span>
            )}
          </div>

          {/* Title + summary */}
          <div
            className={[
              titleClass,
              'mt-sd-1 truncate',
              item.unread ? 'font-semibold text-sd-text' : 'font-medium text-sd-text',
            ].join(' ')}
          >
            {item.title}
          </div>
          <div className={summaryClass}>{item.summary}</div>

          {/* Key ask chip — what the agent actually needs */}
          {!resolved && density !== 'compact' && (
            <div className="mt-sd-2 inline-flex items-center gap-sd-1 rounded-full bg-sd-surface-elevated px-sd-2 py-[2px] text-sd-xs text-sd-text-secondary">
              <span aria-hidden>↳</span>
              <span className="truncate max-w-[22rem]">{item.key_ask}</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
